import httpRequestHandler from '../utils/httpRequestHandler';

// Hourly service types are priced as avg service rate x hours instead of by
// mileage. Must match HOURLY_SERVICE_TYPES in the API's utils/oonCalculator.
export const HOURLY_SERVICE_TYPES = Object.freeze(['HDT']);

export const isHourlyServiceType = (serviceType) =>
    HOURLY_SERVICE_TYPES.includes(String(serviceType || '').trim().toUpperCase());

const _oonCache = new Map();
const TTL_MS = 5 * 60 * 1000;

const _get = (key) => {
    const entry = _oonCache.get(key);
    if (!entry) return undefined;
    if (Date.now() > entry.expiresAt) {
        _oonCache.delete(key);
        return undefined;
    }
    return entry.value;
};

/**
 * Clears cached state and service-type lists. Wired into signOut alongside the
 * other service caches so nothing outlives the session.
 */
export function clearOonCalculatorServiceCache() {
    _oonCache.clear();
}

const unwrap = (data) => data?.data ?? data;

class OonCalculatorService {
    #cachedGet = async (path, params) => {
        const key = `${path}:${JSON.stringify(params || {})}`;
        const cached = _get(key);
        if (cached !== undefined) return cached;

        const { data } = await httpRequestHandler.get(path, params ? { params } : undefined);
        const value = unwrap(data);
        _oonCache.set(key, { value, expiresAt: Date.now() + TTL_MS });
        return value;
    };

    getStates = () => this.#cachedGet('/oon-calculator/states');

    getServiceTypes = (state) => this.#cachedGet('/oon-calculator/service-types', { state });

    // Not cached: averages move daily and the call is cheap.
    calculate = async ({ state, serviceType, loadedMiles, unloadedMiles, hours }) => {
        const body = isHourlyServiceType(serviceType)
            ? { state, serviceType, hours }
            : { state, serviceType, loadedMiles, unloadedMiles };
        const { data } = await httpRequestHandler.post('/oon-calculator/calculate', body);
        return unwrap(data);
    };
}

const oonCalculatorService = new OonCalculatorService();

export default oonCalculatorService;
